// src/pages/admin/AdminTests.tsx
import React, { useEffect, useState } from 'react';
import { collection, doc, getDocs, getDoc, setDoc, orderBy, query } from 'firebase/firestore';
import { ref as sRef, getDownloadURL } from 'firebase/storage';
import { db, storage, uploadFileWithToken } from '../../lib/firebase';
import { useThemePalette } from '../../hooks/useThemePalette';
import { useThemeStore } from '../../stores/useThemeStore';

interface BookEntry { id: string; title: string; units: number; }

const VERSIONS = ['A', 'B', 'progress', 'final'] as const;
type Version = typeof VERSIONS[number];

const inputStyle = (C: ReturnType<typeof useThemePalette>, isDark: boolean): React.CSSProperties => ({
  padding: '9px 12px', borderRadius: '10px',
  border: `1px solid ${C.border}`,
  background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
  color: C.text, fontSize: '0.85rem', fontFamily: 'inherit', outline: 'none',
});

export default function AdminTests() {
  const C      = useThemePalette();
  const isDark = useThemeStore(s => s.theme) === 'dark';

  const [books,      setBooks]      = useState<BookEntry[]>([]);
  const [bookId,     setBookId]     = useState('');
  const [unitNum,    setUnitNum]    = useState('1');
  const [unitCount,  setUnitCount]  = useState(1);
  const [version,    setVersion]    = useState<Version>('A');
  const [testFile,   setTestFile]   = useState<File | null>(null);
  const [existing,   setExisting]   = useState<string | null>(null);
  const [status,     setStatus]     = useState('');
  const [isErr,      setIsErr]      = useState(false);

  useEffect(() => {
    getDocs(query(collection(db, 'books'), orderBy('displayOrder')))
      .then(snap => {
        const list: BookEntry[] = [];
        snap.forEach(d => list.push({ id: d.id, title: d.data().title as string, units: d.data().units as number ?? 0 }));
        setBooks(list);
      })
      .catch(() => {});
  }, []);

  // Load existing test doc when selection changes
  useEffect(() => {
    setExisting(null);
    if (!bookId) return;
    const book = books.find(b => b.id === bookId);
    setUnitCount(book?.units ?? 1);
    const docId = `${bookId}_unit${unitNum}_test${version}`;
    getDoc(doc(db, 'tests', docId))
      .then(snap => {
        if (snap.exists()) setExisting(snap.data()?.downloadUrl as string ?? null);
      })
      .catch(() => {});
  }, [bookId, unitNum, version, books]);

  const upload = async () => {
    if (!bookId) { setStatus('Select a book'); setIsErr(true); return; }
    if (!testFile) { setStatus('Select a JSON file'); setIsErr(true); return; }
    setStatus('Uploading...'); setIsErr(false);
    try {
      const path = `tests/${bookId}/unit${unitNum}/test${version}_${Date.now()}.json`;
      const storageRef = sRef(storage, path);
      await uploadFileWithToken(storageRef, testFile);
      const downloadUrl = await getDownloadURL(storageRef);

      const docId = `${bookId}_unit${unitNum}_test${version}`;
      await setDoc(doc(db, 'tests', docId), { downloadUrl, updatedAt: Date.now() });

      setExisting(downloadUrl);
      setTestFile(null);
      setStatus('Uploaded!'); setIsErr(false);
      setTimeout(() => setStatus(''), 3000);
    } catch (e: unknown) {
      setStatus(e instanceof Error ? e.message : 'Upload failed');
      setIsErr(true);
    }
  };

  const unitOptions = Array.from({ length: unitCount }, (_, i) => i + 1);

  return (
    <div style={{ padding: '32px', maxWidth: '680px' }}>
      <div style={{ fontSize: '1.1rem', fontWeight: 800, color: C.text, marginBottom: '8px' }}>Manage Tests</div>
      <div style={{ fontSize: '0.8rem', color: C.muted, marginBottom: '28px' }}>Upload test JSON files to Firebase Storage and register in Firestore</div>

      {/* Selection row */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <select
          value={bookId}
          onChange={e => { setBookId(e.target.value); setUnitNum('1'); setVersion('A'); setExisting(null); }}
          style={{ ...inputStyle(C, isDark), flex: '1 1 180px' }}
        >
          <option value="">— Select Book —</option>
          {books.map(b => <option key={b.id} value={b.id}>{b.title}</option>)}
        </select>

        <select
          value={unitNum}
          onChange={e => setUnitNum(e.target.value)}
          disabled={!bookId}
          style={{ ...inputStyle(C, isDark), flex: '0 0 100px' }}
        >
          {unitOptions.map(n => <option key={n} value={String(n)}>Unit {n}</option>)}
        </select>

        <select
          value={version}
          onChange={e => setVersion(e.target.value as Version)}
          disabled={!bookId}
          style={{ ...inputStyle(C, isDark), flex: '0 0 120px' }}
        >
          {VERSIONS.map(v => <option key={v} value={v}>Test {v}</option>)}
        </select>
      </div>

      {bookId && (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '16px', padding: '24px' }}>
          <div style={{ fontSize: '0.82rem', fontWeight: 700, color: C.text, marginBottom: '6px' }}>
            {bookId}_unit{unitNum}_test{version}
          </div>

          {existing ? (
            <div style={{ fontSize: '0.75rem', color: C.success, marginBottom: '16px', wordBreak: 'break-all' }}>
              Current file: {existing.split('/').pop()?.split('?')[0] ?? existing}
            </div>
          ) : (
            <div style={{ fontSize: '0.75rem', color: C.muted, marginBottom: '16px' }}>No test uploaded yet for this selection.</div>
          )}

          <label style={{ fontSize: '0.82rem', fontWeight: 700, color: C.text, display: 'block', marginBottom: '8px' }}>
            {existing ? 'Replace Test JSON' : 'Upload Test JSON'}
          </label>
          <input
            type="file"
            accept="application/json,.json"
            onChange={e => setTestFile(e.target.files?.[0] ?? null)}
            style={{ fontSize: '0.82rem', color: C.text, marginBottom: '16px', display: 'block' }}
          />
          {testFile && (
            <div style={{ fontSize: '0.75rem', color: C.premium, marginBottom: '12px' }}>
              Selected: {testFile.name} ({(testFile.size / 1024).toFixed(1)} KB)
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              onClick={() => void upload()}
              disabled={!testFile}
              style={{
                padding: '8px 20px', borderRadius: '999px', border: 'none',
                background: testFile ? C.accent : C.border, color: testFile ? '#fff' : C.muted,
                fontSize: '0.78rem', fontWeight: 700,
                cursor: testFile ? 'pointer' : 'not-allowed', fontFamily: 'inherit',
              }}
            >
              Upload
            </button>
            {status && <span style={{ fontSize: '0.78rem', color: isErr ? C.error : C.success }}>{status}</span>}
          </div>
        </div>
      )}
    </div>
  );
}
